import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Heading,
  IconLinkLine,
  Modal,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ExternalService } from "../../../../../services/ExternalService";
import {
  ERROR,
  useNotification,
} from "../../../../CustomHooks/NotificationProvider";

const EmbeddedDashboardResults = ({
  measure,
  planId,
  sectionId,
  publicResults,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [url, setUrl] = useState(measure.url);
  const [dashboardError, setDashboardError] = useState(false);

  useEffect(() => {
    if (measure.url) {
      try {
        setDashboardError(false);
        const urlObj = new URL(measure.url);
        const script = document.createElement("script");
        script.src = `https://${urlObj.hostname}/javascripts/api/tableau.embedding.3.latest.min.js`;
        script.type = "module";
        script.onload = () => {
          const viz = document.getElementById(`tableauViz-${measure.ddl_id}`);
          viz.src = measure.url;
        };
        script.onerror = () => {
          setDashboardError(true);
        };
        document.head.appendChild(script);
      } catch {
        setDashboardError(true);
      }
    }
  }, [measure.url]);

  const handleUpdateLink = async (newUrl) => {
    setSaving(true);
    try {
      const newMeasure = {
        id: measure.ddl_id,
        name: measure.name,
        url: newUrl,
        description: measure.description,
        sequence: measure.sequence,
      };
      const dashboard = {
        planId: planId,
        sectionId: sectionId,
        embeddedDatalink: newMeasure,
      };
      await ExternalService.updateExternalDatalink(
        dashboard,
        dashboard.embeddedDatalink.id
      );
      measure.url = newUrl;
      setOpenModal(false);
      showNotification(
        t("notifications.general.updateSuccess", { obj: "Dashboard" })
      );
    } catch {
      showNotification(t("results.embeddedUpdateError"), ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Flex wrap="wrap" gap="small">
      <Flex.Item size="100%">
        <Heading level="h3">{measure.name}</Heading>
      </Flex.Item>
      <Flex.Item padding="0 0 0 large">
        <Text>{measure.description}</Text>
      </Flex.Item>
      {!publicResults && (
        <Flex.Item size="100%" textAlign="end">
          <Button
            renderIcon={IconLinkLine}
            color="primary"
            size="small"
            onClick={() => setOpenModal(true)}
          >
            {t("embeddedDashboard.linkDashboard")}
          </Button>
        </Flex.Item>
      )}
      {measure.url ? (
        dashboardError ? (
          <Flex.Item size="100%">
            <Alert variant="error">{t("results.embeddedShowError")}</Alert>
          </Flex.Item>
        ) : (
          <Flex.Item size="100%" margin="medium 0">
            <tableau-viz
              id={`tableauViz-${measure.ddl_id}`}
              style={{ margin: "0 auto" }}
            ></tableau-viz>
          </Flex.Item>
        )
      ) : (
        <Flex.Item size="100%">
          <Alert variant="info">{t("results.linkDashboardAlert")}</Alert>
        </Flex.Item>
      )}
      <Modal
        open={openModal}
        size="medium"
        label={t("embeddedDashboard.linkDashboard")}
      >
        <Modal.Header>
          <Flex justifyItems="space-between">
            <Flex.Item>
              <Heading as="h3">{t("embeddedDashboard.linkDashboard")}</Heading>
            </Flex.Item>
            <Flex.Item>
              <CloseButton
                offset="small"
                onClick={() => setOpenModal(false)}
                screenReaderLabel={t("global.close")}
              />
            </Flex.Item>
          </Flex>
        </Modal.Header>
        <Modal.Body>
          <TextInput
            renderLabel={
              <Text size="small">{t("embeddedDashboard.dashURL")}</Text>
            }
            size="small"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setOpenModal(false)} margin="0 x-small 0 0">
            {t("global.cancel")}
          </Button>
          <Button
            onClick={() => handleUpdateLink(url)}
            color="primary"
            type="submit"
            disabled={saving}
          >
            {saving ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("global.save")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Flex>
  );
};

export default EmbeddedDashboardResults;
